import { CellContext, createColumnHelper } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { PATH } from 'src/features/navigation/utils/constants';
import useTransformLinkedEntitiesResponse from 'src/features/pricingPlan/api/linkedEntities/useTransformLinkedEntitiesResponse';
import { linkedEntitiesTypeBadgeOptions } from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { LINKED_ENTITIES_KEYS, PricingPlanUsedInColumns } from 'src/features/pricingPlan/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { LinkWithIcon } from 'src/registry/components/navigation/LinkWithIcon';
import { CellBadge, CellDatetime, DataTable, DataTableControls } from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import useFilters from 'src/registry/hooks/useFilters';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { commonMessages } from 'src/utils/messages';

type PricingPlanUsedInTableProps = {
    pricingPlanIdentity?: string;
};

// Kept out of the table component so the cell renderer has a stable identity across renders -
// declared inline it was remounted on every render, which is also what used to hide the stale
// row id below.
const UsedInNameCell = ({ row }: CellContext<PricingPlanUsedInColumns, unknown>) => {
    const { formatMessage } = useIntl();

    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const accessZones = useHasPermissions([PERMISSION.ACCESS_ZONES]);
    const accessGateways = useHasPermissions([PERMISSION.ACCESS_GATEWAYS]);
    const accessMembership = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIPS]);
    const accessMembershipType = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);

    const { type, name, id, valid_from } = row.original;

    if (!name || name === '-') return '-';

    const entityId = id ?? '';

    const isFuture = dayjs(valid_from).isAfter(dayjs());

    const urlToRouteTo = (() => {
        switch (type) {
            case LINKED_ENTITIES_KEYS.SITE_PRICING_PLANS:
                return `/${PATH.ADMINISTRATION}/site/${entityId}`;
            case LINKED_ENTITIES_KEYS.ZONE_PRICING_PLANS:
                return `/${PATH.ADMINISTRATION}/zone/${entityId}`;
            case LINKED_ENTITIES_KEYS.GATEWAY_PRICING_PLANS:
                return `/${PATH.ADMINISTRATION}/gateway/${entityId}`;
            case LINKED_ENTITIES_KEYS.MEMBERSHIPS:
                return `/${PATH.MEMBERSHIPS}/all/${entityId}`;
            case LINKED_ENTITIES_KEYS.MEMBERSHIP_TYPES:
                return `/${PATH.ADMINISTRATION}/membership-templates/${entityId}`;
            default:
                return '-';
        }
    })();

    const hasPermissions = (() => {
        switch (type) {
            case LINKED_ENTITIES_KEYS.SITE_PRICING_PLANS:
                return accessSites;
            case LINKED_ENTITIES_KEYS.ZONE_PRICING_PLANS:
                return accessZones;
            case LINKED_ENTITIES_KEYS.GATEWAY_PRICING_PLANS:
                return accessGateways;
            case LINKED_ENTITIES_KEYS.MEMBERSHIPS:
                return accessMembership;
            case LINKED_ENTITIES_KEYS.MEMBERSHIP_TYPES:
                return accessMembershipType;
            default:
                return false;
        }
    })();

    return (
        <div className="flex flex-row items-baseline gap-2">
            <LinkWithIcon to={urlToRouteTo} linkTitle={name} hasPermissions={hasPermissions && !!id} />
            {isFuture && (
                <div className="text-xs text-gray-500">{formatMessage(pricingPlanMessages.plannedChanges)}</div>
            )}
        </div>
    );
};

const PricingPlanUsedInTable = ({ pricingPlanIdentity }: PricingPlanUsedInTableProps) => {
    const { formatMessage } = useIntl();

    const { querySortingOptions, ...sorting } = useSorting();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();

    const { data: linkedEntities } = useTransformLinkedEntitiesResponse({
        pricingPlanId: pricingPlanIdentity ?? '',
        querySortingOptions,
        queryFiltersOptions,
        querySearchOptions,
    });

    const { transformedData, total_active_stays, total_active_memberships } = linkedEntities;

    const columnHelper = createColumnHelper<PricingPlanUsedInColumns>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: UsedInNameCell,
            meta: {
                title: formatMessage(commonMessages.name),
            },
            minSize: 140,
        }),
        columnHelper.accessor('type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(commonMessages.type),
                badgeOptions: linkedEntitiesTypeBadgeOptions,
            },
            id: 'filter',
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(commonMessages.activeFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(commonMessages.activeTo),
            },
        }),
        columnHelper.accessor('active_stays_count', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const type = row.original.type;
                const count =
                    type === LINKED_ENTITIES_KEYS.MEMBERSHIP_TYPES
                        ? (row.original.active_memberships_count ?? 0)
                        : (row.original.active_stays_count ?? 0);

                const messageBasedOnType =
                    type === LINKED_ENTITIES_KEYS.MEMBERSHIP_TYPES
                        ? formatMessage(pricingPlanMessages.numberOfActiveMemberships, { count })
                        : formatMessage(pricingPlanMessages.numberOfActiveStays, { count });

                return <div className="font-semibold">{count > 0 ? messageBasedOnType : '-'}</div>;
            },
            meta: {
                title: formatMessage(pricingPlanMessages.affectedNow),
            },
        }),
    ];

    const data = {
        count: linkedEntities.total_entities_count ?? 0,
        offset: 0,
        limit: 0,
        results: transformedData,
    };

    const totalActiveStaysMessage = total_active_stays
        ? formatMessage(pricingPlanMessages.numberOfActiveStays, { count: total_active_stays })
        : '';
    const totalActiveMembershipsMessage = total_active_memberships
        ? formatMessage(pricingPlanMessages.numberOfActiveMemberships, { count: total_active_memberships })
        : '';
    const additionalTableHeader =
        total_active_stays && total_active_memberships
            ? `${totalActiveStaysMessage} - ${totalActiveMembershipsMessage}`
            : `${totalActiveStaysMessage}${totalActiveMembershipsMessage}`;

    return (
        <DataTable<PricingPlanUsedInColumns, any>
            columns={columns}
            data={data}
            enableSearchParams={false}
            search={search}
            sorting={sorting}
            filters={filters}
            semantics={{
                title: formatMessage(pricingPlanMessages.usedIn),
                entryName: formatMessage(pricingPlanMessages.record).toLowerCase(),
                entryNames: formatMessage(pricingPlanMessages.records).toLowerCase(),
                placeholder: formatMessage(pricingPlanMessages.plan).toLowerCase(),
                additionalTableHeader,
            }}
            components={{
                controls: DataTableControls,
            }}
        />
    );
};

export default PricingPlanUsedInTable;
